// ============================================
// Xác nhận đổi trạng thái góp ý
// ============================================
// ⚠️ VÌ SAO PHẢI CÓ BƯỚC NÀY, KỂ CẢ KHI KHÔNG AI ĐƯỢC BÁO:
// `PATCH /feedback/staff/{id}/` kèm `status` luôn ghi `handled_by`/`handled_at`
// lên server — tức là dán TÊN NGƯỜI ĐANG ĐĂNG NHẬP vào bản ghi. Bấm nhầm một
// nút trong lưới bốn nút không phải chuyện hiếm, nên mọi trạng thái đều đi qua
// hộp này. Nhưng NỘI DUNG hộp thì khác nhau, xem dưới.
//
// ⚠️ KHÔNG PHẢI MỌI LẦN ĐỔI TRẠNG THÁI ĐỀU BẮN THÔNG BÁO (BE xác nhận
// 28/08/2026, bác bỏ giả định cũ của FE):
//   RESOLVED  -> CÓ gửi thông báo cho khách
//   DISMISSED -> CÓ gửi thông báo cho khách
//   IN_REVIEW -> IM LẶNG. Lý do của BE: "có người bắt đầu đọc" không phải tin
//                tức đáng làm phiền khách.
//   NEW       -> IM LẶNG.
// Vì thế câu "khách sẽ nhận thông báo / không hoàn tác được" CHỈ được hiện cho
// hai trạng thái đầu. Nói câu đó cho IN_REVIEW là dạy người dùng ngại bấm một
// nút vô hại — rồi hàng đợi không bao giờ được nhận việc.
//
// Thông báo có `collapse_key` theo từng feedback: đổi trạng thái hai lần thì
// banner trên máy khách THAY THẾ CHÍNH NÓ, không xếp chồng. Nên "lỡ bấm
// RESOLVED rồi sửa lại thành DISMISSED" không để lại hai banner mâu thuẫn trên
// màn hình khách — sai sót đó tự khép lại, đừng cảnh báo như thể không.
//
// DISMISSED KHÔNG còn cảnh báo nặng. Nội dung khách thật sự nhận được là TRUNG
// TÍNH, không có chữ nào kiểu "bị bỏ qua" (BE có đủ pl/uk/en):
//   "Twoje zgłoszenie rozpatrzono" / "Your report was reviewed"
//   "Sprawdziliśmy je — tym razem działania nie były potrzebne."
// Nên thay vì một lời hù doạ mơ hồ, hộp này TRÍCH THẲNG nội dung đó ra: người
// xử lý biết chính xác khách sắp đọc gì thì tự quyết định được, hữu ích hơn
// nhiều so với "chỉ dùng cho spam".

import { FeedbackStatus } from '@conai/shared-types';
import { StatusBadge } from '../../../components/shared';
import { FEEDBACK_STATUS_VARIANT, statusNotifiesCustomer, useFeedbackText } from '../feedbackText';

interface StatusChangeConfirmModalProps {
  currentStatus: FeedbackStatus;
  nextStatus: FeedbackStatus;
  isPending: boolean;
  /** Có ghi chú đang gõ dở mà chưa bấm lưu — nói trước để khỏi mất chữ. */
  hasUnsavedNote: boolean;
  /**
   * Có câu TRẢ LỜI đang gõ dở mà chưa gửi.
   *
   * Tách khỏi `hasUnsavedNote` vì hậu quả khác hẳn: mất một ghi chú nội bộ là
   * phiền, còn tưởng đã trả lời khách mà thật ra chưa gửi chữ nào là để khách
   * ngồi chờ một câu không bao giờ tới — trên một bản ghi vừa bị đóng lại.
   */
  hasUnsavedReply: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function StatusChangeConfirmModal({
  currentStatus,
  nextStatus,
  isPending,
  hasUnsavedNote,
  hasUnsavedReply,
  onConfirm,
  onCancel,
}: StatusChangeConfirmModalProps) {
  const { t, tr, statusLabel } = useFeedbackText();

  /** Trạng thái đích này có làm khách nhận thông báo không. Xem docblock đầu file. */
  const notifiesCustomer = statusNotifiesCustomer(nextStatus);
  const isDismissal = nextStatus === FeedbackStatus.DISMISSED;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      // `stopPropagation` BẮT BUỘC: hộp xác nhận được render BÊN TRONG overlay
      // của `FeedbackDetailModal`, mà overlay đó đóng modal khi bị bấm. Thiếu
      // dòng này thì bấm ra ngoài để huỷ sẽ đóng luôn cả màn chi tiết.
      onClick={(event) => {
        event.stopPropagation();
        if (!isPending) onCancel();
      }}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="text-lg font-bold text-slate-900">
          {notifiesCustomer
            ? tr('feedbackConfirmStatusTitle', 'Change status and notify the customer?')
            : tr('feedbackConfirmStatusTitleSilent', 'Change status?')}
        </h3>

        <div className="mt-4 flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
          <StatusBadge variant={FEEDBACK_STATUS_VARIANT[currentStatus] ?? 'neutral'}>
            {statusLabel(currentStatus)}
          </StatusBadge>
          <span className="text-slate-400">→</span>
          <StatusBadge variant={FEEDBACK_STATUS_VARIANT[nextStatus] ?? 'neutral'}>
            {statusLabel(nextStatus)}
          </StatusBadge>
        </div>

        <p className="mt-4 text-sm text-slate-600">
          {notifiesCustomer
            ? tr(
                'feedbackConfirmStatusBody',
                'The customer receives a push notification as soon as you confirm. This cannot be undone.'
              )
            : // KHÔNG nhắc thông báo, KHÔNG nói "không hoàn tác được" — trạng thái
              // này im lặng, và đổi lại được. Điều duy nhất đáng nói là nó ghi tên
              // người xử lý lên server.
              tr(
                'feedbackConfirmStatusBodySilent',
                'This status is not sent to the customer. It records you as the person handling this feedback, with a timestamp.'
              )}
        </p>

        {isDismissal ? (
          // Trích đúng thông báo khách sẽ nhận. Câu cũ
          // (`feedbackConfirmDismissWarning`) mô tả sai nội dung đó nên KHÔNG
          // dùng nữa — xem docblock đầu file.
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <p className="text-sm text-slate-600">
              {tr(
                'feedbackConfirmDismissNotice',
                'Dismissing sends the customer a neutral message — it never says their report was ignored. It cannot be recalled.'
              )}
            </p>
            <div className="mt-2 border-l-2 border-slate-300 pl-3">
              <p className="text-sm font-semibold text-slate-700">
                {tr('feedbackDismissNotificationTitle', 'Your report was reviewed')}
              </p>
              <p className="text-sm text-slate-600">
                {tr(
                  'feedbackDismissNotificationBody',
                  'We looked into it and no action was needed this time.'
                )}
              </p>
            </div>
          </div>
        ) : null}

        {/* Chữ chưa gửi: KHÔNG liên quan tới thông báo, áp cho MỌI trạng thái.
            Một cú PATCH `{ status }` không mang theo chữ đang gõ dở ở hai ô kia.
            Câu trả lời đứng TRƯỚC vì nó là thứ khách đang chờ. */}
        {hasUnsavedReply ? (
          <p className="mt-3 text-xs font-semibold text-amber-700">
            {tr(
              'feedbackConfirmUnsavedReply',
              'Your reply to the customer has not been sent yet and will not be sent with this change. Send it first.'
            )}
          </p>
        ) : null}
        {hasUnsavedNote ? (
          <p className="mt-3 text-xs text-amber-600">
            {tr(
              'feedbackConfirmUnsavedNote',
              'Your internal note has not been saved yet and will not be sent with this change. Save it separately.'
            )}
          </p>
        ) : null}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-40"
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            // Một màu cho cả bốn trạng thái: nút đỏ từng đi kèm câu cảnh báo
            // "khách sẽ biết phản ánh bị bỏ qua", mà câu đó sai. Giữ đỏ trong khi
            // chữ bên trên nói "thông báo trung tính" là hai tín hiệu ngược nhau.
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-60"
          >
            {isPending
              ? t('processing')
              : notifiesCustomer
              ? tr('feedbackConfirmStatusAction', 'Confirm and notify')
              : tr('feedbackConfirmStatusActionSilent', 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
